// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import frameHtml from './frame.html?resource';
import { resolveManifestBaseUrl, resolveSameOriginFrameSrc } from './utils.js';

/**
 * Options for creating an iframe for a guest application
 */
export type FrameOptions = {
  /**
   * title of the iframe
   */
  title: string;
  /**
   * sandbox attribute for the iframe
   * check https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-sandbox for more details
   */
  sandbox?: string;
  /**
   * source url for the iframe
   */
  src?: string;
  /**
   * query parameters to be appended to the iframe src url
   */
  queryParams?: string;
  /**
   * css style for the iframe
   * @default 'flex-grow: 1;border: none;margin: 0;padding: 0;display: block;min-width: 100%;height: 100%;'
   */
  style?: string;
  /**
   * unique identified of the iframe container
   */
  containerId?: string;
  /**
   * permission policy for the guest iframe
   * check https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/iframe#allow for more details
   * @example "microphone 'self' https://example.com; geolocation 'none'"
   */
  permissionPolicy?: string;
  /**
   * Optional signal to abort iframe creation (e.g. when the host unmounts
   * before the iframe finishes loading).
   */
  signal?: AbortSignal;
};

export const FRAME_APP_CONTAINER_ID_PREFIX = 'pui-app-container-';

/**
 * Builds the final iframe `src` passed to {@link Frame.create}.
 *
 * Resolves the base URL from `options.src` or the bundled `frame.html`
 * resource, applies {@link resolveSameOriginFrameSrc} for nested hosts, then
 * optionally appends sanitized query parameters (e.g. LogRocket params).
 * @param options - Frame creation options; only `src` and `queryParams` are
 *   used by this helper.
 * @returns Fully qualified iframe src, with query string when provided.
 * @example
 * // Parent opens child (nested under grandparent); see resolveSameOriginFrameSrc.
 * buildFrameSrc({ title: 'Incomplete Apps' });
 * // => 'http://localhost:3000/latest/frame.html'
 * @example
 * // Caller-supplied src plus query params.
 * buildFrameSrc({
 *   title: 'Incomplete Apps',
 *   src: 'http://localhost:4004/latest/frame.html',
 *   queryParams: 'lr=abc123',
 * });
 * // => 'http://localhost:3000/latest/frame.html?lr=abc123'
 */
const buildFrameSrc = (options: FrameOptions): string => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const rawSrc = options.src ?? ((frameHtml.default ?? frameHtml) as string);
  const baseSrc = resolveSameOriginFrameSrc(rawSrc);
  if (!options.queryParams) return baseSrc;
  const sanitized = options.queryParams.replace(/[^a-zA-Z0-9&=_.~%+-]/g, '');
  return `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}${sanitized}`;
};

/**
 * Prepares a child guest iframe document for manifest and asset loading.
 *
 * Called from {@link Frame.create} once the child iframe has loaded and
 * `contentDocument` is accessible (same-origin — see
 * {@link resolveSameOriginFrameSrc}).
 *
 * The child iframe initially loads a generic `frame.html` shell from the
 * **parent document's origin** (e.g. grandparent's `:3000` when nested).
 * Without a `<base href>`, relative script URLs in that document would resolve
 * against the shell origin, not the **child guest's** dev-server or CDN
 * (`hostUrl`). This function injects or updates `<base href>` so subsequent
 * resource loads (manifest, JS bundles, CSS) target the child's
 * `hostUrl` + `manifestPath`.
 *
 * It also suffixes the mount container (`pui-app-container-`) with
 * `guestId` so multiple guest instances in the same shell document get
 * unique DOM ids.
 * @param params.documentEle - The loaded child iframe's `contentDocument`.
 * @param params.hostUrl - Guest app's configured base URL (from
 *   `microFrontendApps.<id>.hostUrl`).
 * @param params.manifestPath - Versioned manifest directory (e.g. `./latest/`).
 * @param params.guestId - Guest app id; appended to the mount container element id.
 * @param params.documentEle.documentEle
 * @param params.documentEle.hostUrl
 * @param params.documentEle.manifestPath
 * @param params.documentEle.guestId
 * @example
 * // Grandparent → parent → child. Child iframe shell is :3000; assets come from :8080.
 * appendManifestBaseTag({
 *   documentEle: childIframe.contentDocument,
 *   hostUrl: 'http://localhost:8080/incomplete-apps/',
 *   manifestPath: './latest/',
 *   guestId: 'incomplete-apps',
 * });
 * // => <base href="http://localhost:8080/incomplete-apps/latest/">
 * // => <div id="pui-app-container-incomplete-apps">
 */
const appendManifestBaseTag = ({
  documentEle,
  hostUrl,
  manifestPath,
  guestId,
}: {
  documentEle: Document;
  hostUrl: string;
  manifestPath: string;
  guestId: string;
}) => {
  const containerEle = documentEle.getElementById(
    FRAME_APP_CONTAINER_ID_PREFIX,
  );
  if (containerEle) {
    containerEle.id = `${containerEle.id}${guestId}`;
  }
  const manifestBaseUrl = resolveManifestBaseUrl(hostUrl, manifestPath);
  let baseTag = documentEle.getElementsByTagName('base')?.[0];
  if (baseTag) {
    baseTag.href = manifestBaseUrl;
    return;
  }
  baseTag = documentEle.createElement('base');
  baseTag.href = manifestBaseUrl;
  documentEle.getElementsByTagName('head')[0].appendChild(baseTag);
};

/**
 * Creates, mounts, and initializes the iframe shell for a guest micro-app.
 *
 * Used internally by App Bridge when a **parent** host opens a **child** guest
 * (e.g. prospects opening incomplete-apps). The flow:
 *
 * 1. Build iframe `src` via {@link buildFrameSrc} (same-origin shell when nested
 * under a grandparent — see {@link resolveSameOriginFrameSrc}).
 * 2. Append the iframe to `options.containerId` or `document.body`.
 * 3. Wait for the iframe `load` event and a non-null `contentDocument`.
 * 4. Call {@link appendManifestBaseTag} so child assets resolve from `hostUrl`.
 * 5. Resolve with the ready iframe element.
 *
 * Rejects with `AbortError` when `options.signal` is aborted (e.g. host
 * unmounts before load completes). Rejects after 10s if the iframe never
 * becomes accessible — commonly caused by a cross-origin `frame.html` src
 * before same-origin rewriting was applied.
 * @param params.id - Guest app id from `microFrontendApps` (used for
 *   `data-testid` and mount container suffix).
 * @param params.instanceId - Unique iframe element id for this open instance.
 * @param params.manifestPath - Versioned manifest directory from guest config.
 * @param params.hostUrl - Guest app's base URL; drives `<base href>` injection.
 * @param params.options - Iframe attributes, container target, query params, and
 *   optional abort signal.
 * @param params.id.id
 * @param params.id.instanceId
 * @param params.id.manifestPath
 * @param params.id.hostUrl
 * @param params.id.options
 * @returns Promise that resolves to the loaded iframe once `contentDocument` is
 *   ready and the manifest base tag has been applied.
 * @example
 * // Parent host opens a child guest.
 * const frame = await Frame.create({
 *   id: 'incomplete-apps',
 *   instanceId: 'a1b2c3d4-...',
 *   manifestPath: './latest/',
 *   hostUrl: 'http://localhost:8080/incomplete-apps/',
 *   options: { title: 'Incomplete Apps', containerId: 'guest-container' },
 * });
 * // frame.contentDocument is ready; manifest loads from :8080
 */
const create = ({
  id,
  instanceId,
  manifestPath,
  hostUrl,
  options,
}: {
  id: string;
  instanceId: string;
  manifestPath: string;
  hostUrl: string;
  options: FrameOptions;
}): Promise<HTMLIFrameElement> =>
  // eslint-disable-next-line max-statements
  new Promise((resolve, reject) => {
    const { signal } = options;
    if (signal?.aborted) {
      reject(new DOMException('iframe creation aborted', 'AbortError'));
      return;
    }

    const iframeContainer = document.createElement('div');
    iframeContainer.setAttribute(
      'style',
      'display: flex;width: 100%;height: 100%;flex-direction: column;overflow: hidden;',
    );
    const frame = document.createElement('iframe');
    frame.setAttribute('id', instanceId);
    frame.setAttribute('data-testid', id);
    frame.setAttribute('title', options.title);
    frame.setAttribute('allowfullscreen', 'true');
    frame.setAttribute('allowtransparency', 'true');
    if (options.permissionPolicy)
      frame.setAttribute('allow', options.permissionPolicy);
    if (options.sandbox) frame.setAttribute('sandbox', options.sandbox);
    frame.setAttribute(
      'style',
      options.style ??
        'flex-grow: 1;border: none;margin: 0;padding: 0;display: block;min-width: 100%;height: 100%;',
    );
    frame.setAttribute('src', buildFrameSrc(options));
    const FRAME_LOAD_TIMEOUT_MS = 10000;
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    let onAbort: () => void;

    const cleanup = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
    };

    const rejectIfAborted = () => {
      if (!signal?.aborted) return false;
      if (!settled) {
        settled = true;
        cleanup();
        iframeContainer.remove();
        reject(new DOMException('iframe creation aborted', 'AbortError'));
      }
      return true;
    };

    onAbort = () => {
      rejectIfAborted();
    };

    if (rejectIfAborted()) return;

    signal?.addEventListener('abort', onAbort);

    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        iframeContainer.remove();
        reject(
          new Error(
            `iframe for ${id} failed to load within ${FRAME_LOAD_TIMEOUT_MS}ms`,
          ),
        );
      }
    }, FRAME_LOAD_TIMEOUT_MS);

    frame.addEventListener('error', () => {
      if (!settled) {
        settled = true;
        cleanup();
        iframeContainer.remove();
        reject(new Error(`iframe for ${id} failed to load`));
      }
    });

    frame.addEventListener('load', () => {
      if (settled || rejectIfAborted()) return;
      // contentDocument can be null when the browser fires a load event
      // for an intermediate redirect (e.g. 307) before the final response.
      // Skip and wait for the next load event; the timeout guards against
      // the case where it never arrives.
      if (!frame.contentDocument) return;
      settled = true;
      cleanup();
      appendManifestBaseTag({
        documentEle: frame.contentDocument,
        hostUrl,
        manifestPath,
        guestId: id,
      });
      resolve(frame);
    });
    iframeContainer.appendChild(frame);
    const { containerId } = options;
    const parentElement =
      document.getElementById(containerId ?? '') ?? document.body;
    parentElement.appendChild(iframeContainer);
  });

const get = (instanceId: string) =>
  document.getElementById(instanceId) as HTMLIFrameElement;

const remove = (instanceId: string) => {
  const frameEle = get(instanceId);
  if (frameEle) {
    // remove the wrapper div container (which also removes the iframe inside it)
    frameEle.parentElement?.remove();
  }
};

export const Frame = {
  create,
  remove,
  get,
};
